import { useEffect, useMemo, useState } from "react";
import { Button, Modal, Select, Tag, Tooltip } from "antd";
import {
  DownOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { FeatureTypes } from "entity-app/constants";
import ComputeStrategyTag from "components/manage-feature-wf-templates/widgets/compute-strategy-tag";
import {
  ComputeStrategy,
  TMilestonesTemplate,
  TSaveWorkflowModelInput,
  TWorkflowTemplate
} from "./model";
import "./index.scss";
import EditableInput from "./components/editable-input";
import MilestoneEditor from "./milestone-editor";
import { VRules } from "./validation-rules";
import {
  transformEditTemplateToSaveModel,
  transformMilestoneToEditMilestone
} from "./data-transformations";
import { COMPUTE_STRATEGY_LABELS, ErrorMessages } from "../../constants";

function WorkflowTemplateEditor({
  workflowTemplate,
  milestones,
  disable = false,
  isTemplateUsed = false,
  openInEditMode = false,
  otherTemplateNames,
  onSave,
  featureId
}: {
  workflowTemplate: TWorkflowTemplate;
  milestones: Array<TMilestonesTemplate>;
  disable?: boolean;
  isTemplateUsed?: boolean;
  openInEditMode?: boolean;
  otherTemplateNames: Array<string>;
  onSave: (data: TSaveWorkflowModelInput) => Promise<void>;
  featureId: FeatureTypes;
}) {
  const [editMode, setEditMode] = useState(openInEditMode);
  const fieldsDisabled = !editMode || disable;
  const [workflowTemplateState, setWorkflowTemplateState] =
    useState<TWorkflowTemplate>({
      name: workflowTemplate.name,
      id: workflowTemplate.id,
      feature_id: workflowTemplate.feature_id,
      float: 0,
      feature: { show_float: true },
      dateblock_compute_strategy: workflowTemplate.dateblock_compute_strategy
    });
  const [milestonesState, setMilestonesState] = useState(
    transformMilestoneToEditMilestone(milestones)
  );
  const [pendingComputeStrategyKey, setPendingComputeStrategyKey] =
    useState<ComputeStrategy | null>(null);

  const [float, setFloat] = useState<number>(workflowTemplate.float);
  const [saveLoading, setSaveLoading] = useState(false);

  const enableComputeStrategyFeature = featureId === FeatureTypes.MATERIALS;

  const DATE_LOGIC_OPTIONS = [
    {
      value: ComputeStrategy.DURATION_DRIVEN,
      label: COMPUTE_STRATEGY_LABELS[ComputeStrategy.DURATION_DRIVEN].label,
      description:
        COMPUTE_STRATEGY_LABELS[ComputeStrategy.DURATION_DRIVEN].description,
      templateHint:
        COMPUTE_STRATEGY_LABELS[ComputeStrategy.DURATION_DRIVEN].templateHint
    },
    {
      value: ComputeStrategy.COMMITTED_DATE_DRIVEN,
      label:
        COMPUTE_STRATEGY_LABELS[ComputeStrategy.COMMITTED_DATE_DRIVEN].label,
      description:
        COMPUTE_STRATEGY_LABELS[ComputeStrategy.COMMITTED_DATE_DRIVEN]
          .description,
      templateHint:
        COMPUTE_STRATEGY_LABELS[ComputeStrategy.COMMITTED_DATE_DRIVEN]
          .templateHint
    }
  ];

  const onComputeStrategyChange = (value: ComputeStrategy) => {
    setPendingComputeStrategyKey(value);
  };

  const handleConfirmComputeStrategyChange = () => {
    if (!pendingComputeStrategyKey) return;

    const isChangingToCommitted =
      pendingComputeStrategyKey === ComputeStrategy.COMMITTED_DATE_DRIVEN &&
      workflowTemplateState.dateblock_compute_strategy.key !==
        ComputeStrategy.COMMITTED_DATE_DRIVEN;

    if (isChangingToCommitted) {
      setMilestonesState((prev) =>
        prev.map((milestone, index, array) => ({
          ...milestone,
          duration:
            milestone.offsetName !== null && index === array.length - 1
              ? milestone.duration
              : 10
        }))
      );
    }

    setWorkflowTemplateState((pre: TWorkflowTemplate) => ({
      ...pre,
      dateblock_compute_strategy: {
        key: pendingComputeStrategyKey
      }
    }));
    setPendingComputeStrategyKey(null);
  };

  const handleCancelComputeStrategyChange = () => {
    setPendingComputeStrategyKey(null);
  };

  const onRenameTemplateName = (name: string) => {
    setWorkflowTemplateState((pre) => ({ ...pre, name }));
  };

  useEffect(() => {
    if (milestones && !editMode)
      setMilestonesState(transformMilestoneToEditMilestone(milestones));
  }, [milestones, editMode]);

  useEffect(() => {
    if (workflowTemplate && !editMode)
      setWorkflowTemplateState({
        name: workflowTemplate.name,
        id: workflowTemplate.id,
        feature_id: workflowTemplate.feature_id,
        float: 0,
        feature: { show_float: true },
        dateblock_compute_strategy: workflowTemplate.dateblock_compute_strategy
      });
    setFloat(workflowTemplate.float);
  }, [workflowTemplate, editMode]);

  const onSaveButtonClick = async () => {
    try {
      setSaveLoading(() => true);
      const saveData: any = transformEditTemplateToSaveModel(
        workflowTemplateState.id,
        workflowTemplateState.name,
        workflowTemplateState.feature_id,
        milestonesState,
        float
      );

      saveData.compute_strategy =
        workflowTemplateState.dateblock_compute_strategy.key;

      await onSave(saveData);
    } finally {
      setSaveLoading(false);
      setEditMode(false);
    }
  };

  const onCancelButtonClick = () => {
    setMilestonesState(transformMilestoneToEditMilestone(milestones));
    setWorkflowTemplateState({ ...workflowTemplate });
    setEditMode(false);
  };

  const onEditButtonClick = () => setEditMode(true);

  const editButtonTitle = useMemo(() => {
    if (disable) return ErrorMessages.PermissionNotGranted;
    if (isTemplateUsed) return ErrorMessages.templateAlreadyUsed;
    return "";
  }, [disable, isTemplateUsed]);

  const currentStrategyLabel = DATE_LOGIC_OPTIONS.find(
    (o) => o.value === workflowTemplateState.dateblock_compute_strategy.key
  )?.label;

  const pendingStrategyLabel = DATE_LOGIC_OPTIONS.find(
    (o) => o.value === pendingComputeStrategyKey
  )?.label;

  return (
    <div className="w-full bg-white border border-solid  workflow-template-editor">
      <div className="flex items-center w-full p-2 justify-between border-0 border-b border-solid border-inherit">
        <div className="flex gap-x-8">
          <EditableInput
            className="font-medium text-base whitespace-pre"
            classNameEdit="font-medium text-base"
            input={workflowTemplateState.name}
            onSubmit={onRenameTemplateName}
            rules={VRules.TemplateTitle(otherTemplateNames)}
            disable={fieldsDisabled}
            isTemplateUsed={isTemplateUsed}
            data-testid="workflow-template-edit-name"
          />
          {enableComputeStrategyFeature && (
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-ciq-gray-900 text-sm">Template Logic:</span>
              {editMode ? (
                <Select
                  value={workflowTemplateState.dateblock_compute_strategy.key}
                  onChange={onComputeStrategyChange}
                  optionLabelProp="label"
                  className="min-w-[250px]"
                  classNames={{
                    popup: {
                      root: "[&_.ant-select-item-option]:py-2 [&_.ant-select-item-option]:px-3 [&_.ant-select-item-option-content]:whitespace-normal"
                    }
                  }}
                  data-testid="workflow-template-date-logic-select"
                  disabled={disable || isTemplateUsed}
                  suffixIcon={
                    <>
                      <Tooltip
                        title={
                          DATE_LOGIC_OPTIONS.find(
                            (o) =>
                              o.value ===
                              workflowTemplateState.dateblock_compute_strategy
                                .key
                          )?.templateHint
                        }
                      >
                        <span
                          className="inline-flex mr-1 cursor-default"
                          role="button"
                          tabIndex={0}
                          aria-label="Information"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                            }
                          }}
                        >
                          <InfoCircleOutlined className="text-ciq-gray-700" />
                        </span>
                      </Tooltip>
                      <DownOutlined className="ant-select-arrow-icon !pointer-events-none" />
                    </>
                  }
                >
                  {DATE_LOGIC_OPTIONS.map((opt) => (
                    <Select.Option
                      key={opt.value}
                      value={opt.value}
                      label={opt.label}
                    >
                      <div className="py-1 pr-2">
                        <div className="font-semibold text-sm">{opt.label}</div>
                        <div className="text-xs font-normal text-ciq-gray-700 mt-0.5">
                          {opt.description}
                        </div>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                currentStrategyLabel && (
                  <span className="text-sm">
                    <ComputeStrategyTag
                      label={currentStrategyLabel}
                      hintText={
                        DATE_LOGIC_OPTIONS.find(
                          (o) =>
                            o.value ===
                            workflowTemplateState.dateblock_compute_strategy.key
                        )?.templateHint
                      }
                    />
                  </span>
                )
              )}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {isTemplateUsed && (
            <Tag>Template in use. Editing limited to Milestone Action.</Tag>
          )}
          {editMode ? (
            <div className="flex items-center space-x-2">
              <Button
                className="w-24"
                onClick={onCancelButtonClick}
                disabled={saveLoading}
                data-testid="index-button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                className="w-24"
                onClick={onSaveButtonClick}
                loading={saveLoading}
                data-testid="index-button-save"
              >
                Save
              </Button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Button
                type="primary"
                className="w-24"
                data-testid="edit-selected-workflow-template"
                disabled={disable}
                title={editButtonTitle}
                onClick={onEditButtonClick}
              >
                Edit
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="w-full p-1 overflow-x-auto">
        <div className="flex w-full mr-60">
          {milestonesState.map((t, index) => (
            <MilestoneEditor
              allMilestones={milestonesState}
              setMilestones={setMilestonesState}
              key={t.id}
              index={index}
              milestone={t}
              disable={fieldsDisabled}
              isTemplateUsed={isTemplateUsed}
              floatState={{ float, setFloat }}
              featureId={featureId}
              showFloat={workflowTemplate.feature.show_float}
              templateComputeStrategy={
                workflowTemplateState.dateblock_compute_strategy.key
              }
            />
          ))}
        </div>
      </div>
      <Modal
        title={
          <div className="flex items-center gap-2">
            <ExclamationCircleOutlined className="text-ciq-warning-text" />
            <span>Confirm Change?</span>
          </div>
        }
        open={pendingComputeStrategyKey !== null}
        onOk={handleConfirmComputeStrategyChange}
        onCancel={handleCancelComputeStrategyChange}
        okButtonProps={{ className: "w-24", type: "primary" }}
        cancelButtonProps={{ className: "w-24" }}
        okText="Continue"
        cancelText="Cancel"
        width={480}
        className="workflow-template-editor"
        data-testid="workflow-template-compute-strategy-confirm-modal"
      >
        <div className="space-y-2">
          <p className="text-sm text-ciq-gray-975">
            Are you sure you want to change the template logic
            {currentStrategyLabel && pendingStrategyLabel ? (
              <>
                {" "}
                from <strong>{currentStrategyLabel}</strong> to{" "}
                <strong>{pendingStrategyLabel}</strong>?
              </>
            ) : (
              " ?"
            )}
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default WorkflowTemplateEditor;
